/**
 * F 397 — THE SESSION TOKEN STORE.
 *
 * WHY THIS FILE EXISTS, AND WHY IT IS NOT WHAT THE DISPATCH ASKED FOR
 * ---------------------------------------------------------------
 * The dispatch asked for a random `crypto.randomUUID()` token saved to
 * IndexedDB, and for that token to "unlock the CredentialStore" on reload
 * without asking for the password.
 *
 * That specific mechanism cannot work, and it is worth being precise about
 * WHY rather than shipping something that looks like it and silently fails:
 *
 *   - a stored credential is sealed with AES-GCM under
 *     `PBKDF2(passphrase, passphraseSalt(principalId))`
 *     (`derivePassphraseKey`, session.ts:290-306);
 *   - reading it calls `decryptWithKeyFor(rec, keyB64)`, which imports that
 *     raw key and decrypts (credentialStore.ts:540-553);
 *   - PBKDF2 is one-way. A token cannot reproduce the key, and a key
 *     resolver cannot be given "a token instead of a passphrase" — there is
 *     no code path that accepts one.
 *
 * Measured rather than argued: a test derived the key from the real
 * passphrase and from the token with the same salt and asserted they differ
 * (see `f397-session-token.spec.ts`).
 *
 * SO WHAT IS STORED INSTEAD
 * -------------------------
 * The DERIVED KEY — never the passphrase. That is the one material that
 * (a) is not the secret, and (b) can actually reopen the store after a
 * reload. The bounds say "do not save the plaintext passphrase to disk"; this
 * satisfies that literally and in spirit.
 *
 * THE SECURITY TRADE, STATED PLAINLY
 * ----------------------------------
 * Persisting the derived key moves the bar from "attacker needs the
 * password" to "attacker needs the browser profile's IndexedDB". That is a
 * REAL reduction in strength, and anyone reading this file should know it:
 * F 374 replaced a public `VITE_*` key with a passphrase-derived one, and
 * this change gives back part of what that bought.
 *
 * What it does NOT do, and the reason this is defensible at all:
 *   - the passphrase itself is never written, logged or transmitted, so a
 *     shoulder-surfed or reused password still buys nothing extra;
 *   - the key is bound to the principal id in the same way the KDF salt is,
 *     so a record for one person cannot be opened by another's token;
 *   - the key is held in module memory once read, exactly as the passphrase
 *     was, so a locked tab still has nothing to read;
 *   - the value lives in IndexedDB only, never localStorage, and never in a
 *     URL.
 *
 * THIS IS A DELIBERATE, DOCUMENTED TRADE — not a free win. If the operator
 * wants the stronger of the two properties, `logout` below removes the
 * record, and the wall on reload returns. That is the intended escape hatch.
 */
import { derivePassphraseKey, passphraseSalt } from './session';

const DB_NAME = 'freshcards:session';
const STORE = 'sessions';
const VERSION = 1;
const RECORD_ID = 'current';

export interface SessionRecord {
  id: string;
  /** The principal this key belongs to. Bound, never trusted blindly. */
  principalId: string;
  /** The KDF-derived AES key, base64. NEVER the passphrase. */
  derivedKey: string;
  /**
   * F 425 — THE SALT `derivedKey` WAS DERIVED WITH.
   *
   * The key alone is not enough for a third party to reproduce. Anything sealing
   * under this session key must record THIS salt, not a fresh random one, or the
   * salt on the node will not match the salt behind the key and every reader
   * fails to open it. It is written here so the store never has to guess.
   */
  saltB64?: string;
  /** When it was minted, epoch ms. Informational. */
  createdAt: number;
  /** Which KDF produced `derivedKey` — recorded so a future change can tell. */
  kdf: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = run(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
        t.oncomplete = () => db.close();
      }),
  );
}

/**
 * Mint and persist a session record for a signed-in principal.
 *
 * Takes the PASSPHRASE only long enough to derive the key it needs, and does
 * not retain it. The caller is `AuthContext`, which already holds the
 * passphrase; nothing here can read it back out again.
 */
export async function saveSession(passphrase: string, principalId: string): Promise<SessionRecord | null> {
  if (!passphrase || !principalId) return null;
  try {
    const saltB64 = passphraseSalt(principalId);
    const derivedKey = await derivePassphraseKey(passphrase, principalId, saltB64);
    const record: SessionRecord = {
      id: RECORD_ID,
      principalId,
      derivedKey,
      saltB64,
      createdAt: Date.now(),
      kdf: 'pbkdf2',
    };
    await tx('readwrite', (s) => s.put(record));
    return record;
  } catch {
    // A storage failure must never block sign-in. The user is unlocked; they
    // simply get the login screen again next reload, which is the old
    // behaviour and not a regression.
    return null;
  }
}

/** Read the session record, or null when there is none / it is unreadable. */
export async function loadSession(): Promise<SessionRecord | null> {
  try {
    const rec = (await tx<SessionRecord | undefined>('readonly', (s) => s.get(RECORD_ID))) ?? null;
    if (!rec) return null;
    // Shape-check before anything is allowed to use it as a key. A record with
    // no principal or no key is not a session, it is debris.
    if (typeof rec.principalId !== 'string' || !rec.principalId) return null;
    if (typeof rec.derivedKey !== 'string' || !rec.derivedKey) return null;
    return rec;
  } catch {
    return null;
  }
}

/**
 * Forget the session. This is the real logout, and it is what makes the
 * weaker property in the header comment a CHOICE rather than a trap: call
 * this and the wall on reload comes back.
 */
export async function clearSession(): Promise<void> {
  try {
    await tx('readwrite', (s) => s.delete(RECORD_ID));
  } catch {
    // Nothing to do. A failure here means the record was never readable.
  }
}

/** True when a session exists — an availability probe, not a validity check. */
export async function hasSession(): Promise<boolean> {
  return (await loadSession()) !== null;
}